// src/redux/sessionMaster/sessionMasterSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addSessionMasterAPI,
  updateSessionMasterAPI,
  getSessionMasterListAPI,
  getSessionMasterByIdAPI,
  archiveSessionMasterAPI,
  ToolAPI,
} from "./sessionMasterApi";


// ✅ Add
export const addSessionMaster = createAsyncThunk(
  "session/add",
  async (data, { rejectWithValue }) => {
    try {
      const response = await addSessionMasterAPI(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add Session");
    }
  }
);

// ✅ Update
export const updateSessionMaster = createAsyncThunk(
  "sessionMaster/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await updateSessionMasterAPI({ id, data });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update Session");
    }
  }
);

// ✅ List (Updated with access_group_id and archived parameter)
export const getSessionMasterList = createAsyncThunk(
  "sessionMaster/list",
  async (params, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search = "", access_group_id, archived } = params || {};
      const response = await getSessionMasterListAPI({ page, limit, search, access_group_id, archived });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch Session list");
    }
  }
);

// ✅ Detail
export const getSessionMasterDetail = createAsyncThunk(
  "sessionMaster/detail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getSessionMasterByIdAPI(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch Session detail");
    }
  }
);

// ✅ Archive (Updated from delete to archive)
export const archiveSessionMaster = createAsyncThunk(
  "sessionMaster/archive",
  async ({ id, is_archive = true }, { rejectWithValue }) => {
    try {
      const response = await archiveSessionMasterAPI(id, is_archive);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to archive Session");
    }
  }
);

// ✅ Tool List
export const Toollist = createAsyncThunk(
  "sessionMaster/tool",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ToolAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch tools");
    }
  }
);

const initialState = {
  addSessionMaster: { data: null, loading: false, error: null },
  updateSessionMaster: { data: null, loading: false, error: null },
  getSessionMasterList: { data: [], total: 0, loading: false, error: null },
  getSessionMasterDetail: { data: null, loading: false, error: null },
  archiveSessionMaster: { data: null, loading: false, error: null },
  toolList: { data: [], loading: false, error: null },
};

const sessionMasterSlice = createSlice({
  name: "sessionMaster",
  initialState,
  reducers: {
    clearAddSessionMaster: (state) => {
      state.addSessionMaster.data = null;
      state.addSessionMaster.error = null;
    },
    clearUpdateSessionMaster: (state) => {
      state.updateSessionMaster.data = null;
      state.updateSessionMaster.error = null;
    },
    clearSessionMasterDetail: (state) => {
      state.getSessionMasterDetail.data = null;
      state.getSessionMasterDetail.error = null;
    },
    clearArchiveSessionMaster: (state) => {
      state.archiveSessionMaster.data = null;
      state.archiveSessionMaster.error = null;
    },
    clearTool: (state) => {
      state.toolList.data = null;
      state.toolList.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add
      .addCase(addSessionMaster.pending, (state) => {
        state.addSessionMaster.loading = true;
        state.addSessionMaster.error = null;
      })
      .addCase(addSessionMaster.fulfilled, (state, action) => {
        state.addSessionMaster.loading = false;
        state.addSessionMaster.data = action.payload;
      })
      .addCase(addSessionMaster.rejected, (state, action) => {
        state.addSessionMaster.loading = false;
        state.addSessionMaster.error = action.payload;
      })

      // Update
      .addCase(updateSessionMaster.pending, (state) => {
        state.updateSessionMaster.loading = true;
      })
      .addCase(updateSessionMaster.fulfilled, (state, action) => {
        state.updateSessionMaster.loading = false;
        state.updateSessionMaster.data = action.payload;
      })
      .addCase(updateSessionMaster.rejected, (state, action) => {
        state.updateSessionMaster.loading = false;
        state.updateSessionMaster.error = action.payload;
      })

      // List
      .addCase(getSessionMasterList.pending, (state) => {
        state.getSessionMasterList.loading = true;
      })
      .addCase(getSessionMasterList.fulfilled, (state, action) => {
        state.getSessionMasterList.loading = false;
        state.getSessionMasterList.data = action.payload?.data || [];
        state.getSessionMasterList.total =
          action.payload?.total ||
          action.payload?.count ||
          (action.payload?.data ? action.payload.data.length : 0);
      })
      .addCase(getSessionMasterList.rejected, (state, action) => {
        state.getSessionMasterList.loading = false;
        state.getSessionMasterList.error = action.payload;
      })

      // Detail
      .addCase(getSessionMasterDetail.pending, (state) => {
        state.getSessionMasterDetail.loading = true;
      })
      .addCase(getSessionMasterDetail.fulfilled, (state, action) => {
        state.getSessionMasterDetail.loading = false;
        state.getSessionMasterDetail.data = action.payload?.data || action.payload;
      })
      .addCase(getSessionMasterDetail.rejected, (state, action) => {
        state.getSessionMasterDetail.loading = false;
        state.getSessionMasterDetail.error = action.payload;
      })

      // Archive
      .addCase(archiveSessionMaster.pending, (state) => {
        state.archiveSessionMaster.loading = true;
      })
      .addCase(archiveSessionMaster.fulfilled, (state, action) => {
        state.archiveSessionMaster.loading = false;
        state.archiveSessionMaster.data = action.payload;
      })
      .addCase(archiveSessionMaster.rejected, (state, action) => {
        state.archiveSessionMaster.loading = false;
        state.archiveSessionMaster.error = action.payload;
      })

      // Tool List
      .addCase(Toollist.pending, (state) => {
        state.toolList.loading = true;
        state.toolList.error = null;
      })
      .addCase(Toollist.fulfilled, (state, action) => {
        state.toolList.loading = false;
        state.toolList.data = action.payload?.data || [];
      })
      .addCase(Toollist.rejected, (state, action) => {
        state.toolList.loading = false;
        state.toolList.error = action.payload;
      });
  },
});

export const {
  clearAddSessionMaster,
  clearUpdateSessionMaster,
  clearSessionMasterDetail,
  clearArchiveSessionMaster,
  clearTool,
} = sessionMasterSlice.actions;

export default sessionMasterSlice.reducer;